/**
 * ⚖ S5 — compound uploads render as ONE unit in the Acervo.
 *
 * A WhatsApp conversation export is one upload whose 175 members are
 * interdependent. Before this, they fanned out as 175 independent sources and
 * mixed with every standalone document in the case — two conversations in one
 * case would interleave with nothing separating them. `archive-ingest` is the
 * one component that knows the grouping with certainty, so it stamps it:
 * `node_id` on the head, `parent_id` on every member
 * (`compound-upload-grouping-miniplan.md`).
 *
 * This module is the pure half: files in, a tree out. It renders nothing and
 * knows nothing about React.
 *
 * ---
 *
 * ## Three rules, all of them about NOT losing a document
 *
 * **1. A dangling `parent_id` is a ROOT, not an error and not a hidden row.**
 * The pointer may reference a head this case has not indexed yet, or one that
 * will never exist (a plain zip has no head member — its members are stamped
 * anyway so they gain nesting later with zero re-ingest). Both cases render
 * exactly as they do today. This is the producer's own load-bearing degrade
 * rule and it is also the rule this SPA already lived by for a file with no
 * catalog row at all.
 *
 * **2. A cycle is a ROOT.** The contract permits chaining, so depth is
 * unbounded and a malformed or adversarial chain (`a→b→a`) is reachable data,
 * not a hypothetical. Following it blindly hangs the pane; dropping it loses a
 * lawyer's document. Both nodes surface at top level instead.
 *
 * **3. Grouping changes WHERE a row renders, never WHETHER.** Every input file
 * appears exactly once in the output tree. `countFiles` exists so a caller can
 * assert that against the input length rather than trust it.
 *
 * ## Why the tree is built generically instead of "head + one level"
 *
 * S5 v1 only *needs* one level. But the contract already allows a parent row to
 * carry its own `parent_id`, and a generic build is SHORTER than special-casing
 * depth 1 — the special case would have to decide what to do with a chained row
 * anyway, and every answer to that is a tree.
 */

/**
 * Build the path → facts lookup the Acervo nests on.
 *
 * The join is `processed_ref` → the tree file's `path`, the SAME join the
 * category axis already uses — so nesting rides an existing, proven lookup
 * rather than introducing a second way for the catalog and the tree to
 * disagree. A row the catalog has never heard of simply has no facts, which by
 * rule 1 means it renders flat.
 */
export function nestingFactsByPath(
  rows: readonly { processed_ref?: string; node_id?: string; parent_id?: string }[] | undefined,
): Map<string, NestingFacts> {
  const out = new Map<string, NestingFacts>();
  for (const r of rows ?? []) {
    if (!r.processed_ref) continue;
    const nodeId = r.node_id?.trim() || undefined;
    const parentId = r.parent_id?.trim() || undefined;
    if (!nodeId && !parentId) continue;
    out.set(r.processed_ref, { nodeId, parentId });
  }
  return out;
}

/** What the catalog knows about one file, keyed by its tree path. */
export interface NestingFacts {
  /** This file IS a compound head. */
  nodeId?: string;
  /** This file belongs to the compound whose head carries this `nodeId`. */
  parentId?: string;
}

/** One node of the rendered tree. `children` is empty for an ordinary row. */
export interface NestedNode<T> {
  file: T;
  children: NestedNode<T>[];
}

/** Total files in a subtree, INCLUDING the node itself. */
export function countFiles<T>(node: NestedNode<T>): number {
  return 1 + node.children.reduce((n, c) => n + countFiles(c), 0);
}

/** Files BENEATH a node — what a head badges. 0 for an ordinary row. */
export function countMembers<T>(node: NestedNode<T>): number {
  return countFiles(node) - 1;
}

/**
 * Build the forest.
 *
 * Input order is preserved at every level: the Acervo's existing sort is the
 * caller's business, and re-ordering here would silently override it.
 */
export function buildNestedNodes<T extends { path: string }>(
  files: readonly T[],
  factsFor: (path: string) => NestingFacts | undefined,
): NestedNode<T>[] {
  // Head index. Two files claiming the same `node_id` is malformed; first wins
  // and the second becomes an ordinary row, which keeps both visible.
  const headByNodeId = new Map<string, T>();
  for (const f of files) {
    const id = factsFor(f.path)?.nodeId;
    if (id && !headByNodeId.has(id)) headByNodeId.set(id, f);
  }

  /**
   * The parent this file should hang under, or null for "render at top level".
   *
   * Walks the chain to prove the pointer terminates. A self-reference, a cycle,
   * or a dangling id all resolve to null — the three ways a row would otherwise
   * be lost.
   */
  const parentOf = (file: T): T | null => {
    const parentId = factsFor(file.path)?.parentId;
    if (!parentId) return null;
    const parent = headByNodeId.get(parentId);
    if (!parent || parent.path === file.path) return null;

    const seen = new Set<string>([file.path]);
    let cursor: T | undefined = parent;
    while (cursor) {
      if (seen.has(cursor.path)) return null; // cycle → both ends are roots
      seen.add(cursor.path);
      const nextId: string | undefined = factsFor(cursor.path)?.parentId;
      if (!nextId) break;
      cursor = headByNodeId.get(nextId);
    }
    return parent;
  };

  const nodeByPath = new Map<string, NestedNode<T>>();
  for (const f of files) nodeByPath.set(f.path, { file: f, children: [] });

  const roots: NestedNode<T>[] = [];
  for (const f of files) {
    const node = nodeByPath.get(f.path)!;
    const parent = parentOf(f);
    if (parent) nodeByPath.get(parent.path)!.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/**
 * How many files a group is NOT listing because they are nested elsewhere,
 * keyed by the group they WOULD have appeared in.
 *
 * ⚖ **The amendment that made this slice honest** (`ide`, accepted by
 * `mission-ingest` 2026-08-11). Nesting means a member no longer appears in its
 * own category group: measured on the live 176-source corpus, `Financeiro`
 * drops from **73 to 2** because 71 of those receipts are images inside one
 * WhatsApp chat. That re-count is *correct* — 73 was a misleading picture, they
 * were never 73 independently-filed financial documents — but a lawyer who
 * knows the case holds dozens of Pix receipts opens the group, reads **2**, and
 * concludes they were never filed.
 *
 * A false claim built from a negative, which is the defect this codebase keeps
 * re-deriving. So the group discloses what it is holding back (`+71 em
 * conversas`) and the absence stops being silent.
 *
 * ⚠ Heads are NOT counted: a head is not hidden, it is the thing you click.
 */
export function countHiddenByGroup<T extends { path: string }>(
  roots: readonly NestedNode<T>[],
  groupKeyFor: (file: T) => string,
): Map<string, number> {
  const hidden = new Map<string, number>();
  const walk = (node: NestedNode<T>, depth: number) => {
    if (depth > 0) {
      const key = groupKeyFor(node.file);
      hidden.set(key, (hidden.get(key) ?? 0) + 1);
    }
    for (const c of node.children) walk(c, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  return hidden;
}
